import React, { useEffect } from 'react';
import { theme } from '../styles/theme';

const NMICheckout: React.FC = () => {
  useEffect(() => {
    // Load the NMI script dynamically
    const script = document.createElement('script');
    script.src = 'https://secure.nmi.com/token/CollectCheckout.js';
    script.setAttribute('data-checkout-key', process.env.REACT_APP_NMI_CHECKOUT_KEY || 'checkout_public_sSKbNkB83Qf22yjK2T4wv7qM893GJ5WC');
    document.head.appendChild(script);

    // Add the checkout functionality
    const handleCheckout = () => {
      // Get the source parameter from URL
      const urlParams = new URLSearchParams(window.location.search);
      const sourceParam = urlParams.get('source');

      // Determine success URL based on source parameter
      const successUrl = sourceParam 
        ? `https://www.revenuesocial.com/associate-payment?source=${encodeURIComponent(sourceParam)}&transaction_id={TRANSACTION_ID}`
        : "https://www.revenuesocial.com/payment-success";

      // @ts-ignore - NMI script creates global CollectCheckout
      window.CollectCheckout.redirectToCheckout({
        type: "sale",
        lineItems: [
          {
            lineItemType: "customPayment",
            description: "Digital Romeo Plan",
            currency: "USD"
          },
        ],
        successUrl: successUrl,
        cancelUrl: null,
        customerVault: {
          addCustomer: true
        },
        receipt: {
          showReceipt: true,
          redirectToSuccessUrl: true,
          sendToCustomer: true,
        },
        useKount: false,
        paymentMethods: [
          {
            type: "creditCard",
            use3DSecure: false,
          },
          {
            type: "googlePay",
            use3DSecure: false,
          },
          {
            type: "applePay",
            use3DSecure: false,
          },
        ]
      }).then((error: any) => {
        // Error already handled by the NMI form
      });
    };

    // Wait for script to load and DOM to be ready
    const timer = setTimeout(() => {
      const button = document.getElementById('checkout_button');
      if (button) {
        button.addEventListener('click', handleCheckout);
      }
    }, 1000);

    return () => {
      clearTimeout(timer);
      // Clean up script
      const existingScript = document.querySelector('script[src="https://secure.nmi.com/token/CollectCheckout.js"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
      // Clean up event listener
      const button = document.getElementById('checkout_button');
      if (button) {
        button.removeEventListener('click', handleCheckout);
      }
    };
  }, []);

  const containerStyles: React.CSSProperties = {
    minHeight: '100vh',
    background: theme.colors.background,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: theme.typography.fontFamily,
    padding: theme.spacing.lg
  };

  const cardStyles: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.xl,
    padding: theme.spacing['3xl'],
    boxShadow: theme.shadows.xl,
    textAlign: 'center',
    maxWidth: '500px',
    width: '100%'
  };

  const titleStyles: React.CSSProperties = {
    fontSize: theme.typography.sizes['4xl'],
    fontWeight: theme.typography.weights.bold,
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.md,
    letterSpacing: '-0.02em'
  };

  const subtitleStyles: React.CSSProperties = {
    fontSize: theme.typography.sizes.lg,
    color: theme.colors.text.secondary,
    marginBottom: theme.spacing['2xl'],
    lineHeight: theme.typography.lineHeights.relaxed
  };

  const checkoutButtonStyles: React.CSSProperties = {
    display: 'inline-block',
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.sizes.xl,
    fontWeight: theme.typography.weights.semibold,
    background: `linear-gradient(135deg, ${theme.colors.gradientStart} 0%, ${theme.colors.gradientEnd} 100%)`,
    color: theme.colors.text.inverse,
    border: 'none',
    borderRadius: theme.borderRadius.button,
    padding: `${theme.spacing.md} ${theme.spacing['2xl']}`,
    cursor: 'pointer',
    transition: theme.transitions.base,
    boxShadow: theme.shadows.md,
    minWidth: '200px'
  };

  const backLinkStyles: React.CSSProperties = {
    display: 'inline-block',
    marginTop: theme.spacing.xl,
    color: theme.colors.text.secondary,
    textDecoration: 'none',
    fontSize: theme.typography.sizes.base,
    transition: theme.transitions.fast
  };

  return (
    <div style={containerStyles}>
      <div style={cardStyles}>
        <h1 style={titleStyles}>Secure Checkout</h1>
        <p style={subtitleStyles}>
          Complete your payment securely with our integrated checkout system. 
          Multiple payment methods supported including credit cards, Google Pay, and Apple Pay.
        </p>
        
        <button
          id="checkout_button"
          style={checkoutButtonStyles}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = theme.shadows.lg;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0px)';
            e.currentTarget.style.boxShadow = theme.shadows.md;
          }}
        >
          Proceed to Checkout
        </button>

        <div style={{ marginTop: theme.spacing.xl }}>
          <a 
            href="/" 
            style={backLinkStyles}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = theme.colors.gradientStart;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = theme.colors.text.secondary;
            }}
          >
            ← Back to Dashboard
          </a>
        </div>

        {/* Security badges */}
        <div style={{
          marginTop: theme.spacing.xl,
          paddingTop: theme.spacing.lg,
          borderTop: `1px solid ${theme.colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: theme.spacing.md,
          flexWrap: 'wrap'
        }}>
          <div style={{
            fontSize: theme.typography.sizes.xs,
            color: theme.colors.text.light,
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing.xs
          }}>
            🔒 SSL Secured
          </div>
          <div style={{
            fontSize: theme.typography.sizes.xs,
            color: theme.colors.text.light,
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing.xs
          }}>
            🛡️ PCI Compliant
          </div>
        </div>
      </div>
    </div>
  );
};

export default NMICheckout;